/**
 * Automation's part of the attention inbox (lib/inbox): my open tasks and approvals (the same
 * list as /automation/tasks, worked right there), and automations whose runs failed in the
 * last 24 h — for people who may fix the automation.
 */
import { lazy } from 'react';
import { api, unwrap } from '@/lib/api/client';
import { can } from '@/lib/can';
import { msg, tp } from '@/lib/i18n';
import type { InboxItem, InboxSource } from '@/lib/inbox';
import { automationKeys } from './api';
import type { FlowRunOut, TaskOut } from './types';

// Loaded with the first inbox that shows a task (the inbox list renders it in Suspense).
const TaskActions = lazy(() => import('./tasks/TaskActions').then((m) => ({ default: m.TaskActions })));

const OPEN = { status: 'open' } as const;

export const tasksInbox: InboxSource<TaskOut[]> = {
  key: 'tasks',
  label: msg('Tasks and approvals'),
  order: 10,
  permission: 'flows.task.work',
  module: 'automation',
  bell: true,
  href: '/automation/tasks',
  // The key and request of useTasks({ status: 'open' }): completing a task anywhere refreshes it.
  query: () => ({
    queryKey: automationKeys.tasks(OPEN),
    queryFn: () => unwrap(api.GET('/api/automation/tasks', { params: { query: { ...OPEN, limit: 300 } } })),
    refetchInterval: 120_000,
  }),
  items: (tasks) =>
    tasks
      .filter((x) => x.status === 'open')
      .map((x): InboxItem => {
        const overdue = !!x.due_at && new Date(x.due_at).getTime() < Date.now();
        return {
          id: `task:${x.id}`,
          source: 'tasks',
          kind: x.kind === 'approval' ? msg('Approval') : msg('Task'),
          tone: x.kind === 'approval' ? 'accent' : 'info',
          title: x.title,
          detail: x.body,
          subject: x.subject_type && x.subject_id ? { type: x.subject_type, id: x.subject_id, label: x.subject_label } : null,
          meta: x.flow_name ? [x.flow_name] : undefined,
          at: x.created_at,
          due: x.due_at,
          href: `/automation/tasks?selected=${x.id}`,
          rank: overdue ? 0 : x.kind === 'approval' ? 1 : 2,
          actions: <TaskActions task={x} compact size="sm" />,
        };
      }),
};

const DAY_MS = 86_400_000;

/** Failed runs since the start of the previous hour a day ago: a stable key for an hour. */
function since(): string {
  const hour = 3_600_000;
  return new Date(Math.floor((Date.now() - DAY_MS) / hour) * hour).toISOString();
}

export const failedRunsInbox: InboxSource<FlowRunOut[]> = {
  key: 'failed-runs',
  label: msg('Failed automations'),
  order: 50,
  // Whoever may fix the automation (and read its runs) sees its failures.
  permission: 'flows.flow.manage',
  when: (me) => can(me, 'flows.run.read'),
  module: 'automation',
  href: '/automation/runs?status=failed',
  query: () => {
    const q = { status: 'failed', since: since(), limit: 100 };
    return {
      queryKey: [...automationKeys.all, 'inbox', 'failed-runs', q.since],
      queryFn: () => unwrap(api.GET('/api/automation/runs', { params: { query: q } })),
      refetchInterval: 300_000,
    };
  },
  // One item per automation: its latest failure and how often it failed.
  items: (runs) => {
    const byFlow = new Map<string, FlowRunOut[]>();
    for (const r of runs) byFlow.set(r.flow_id, [...(byFlow.get(r.flow_id) ?? []), r]);
    return [...byFlow.values()].map((list): InboxItem => {
      const last = list[0] as FlowRunOut; // newest first
      return {
        id: `failed-run:${last.flow_id}`,
        source: 'failed-runs',
        kind: msg('Automation failed'),
        tone: 'bad',
        title: last.flow_name ?? last.flow_id,
        detail: last.error,
        subject: last.subject_type && last.subject_id ? { type: last.subject_type, id: last.subject_id, label: last.subject_label } : null,
        meta: [tp(list.length, 'failed once in 24 h', 'failed {count} times in 24 h')],
        at: last.finished_at ?? last.started_at,
        href: `/automation/runs/${last.id}`,
        rank: 5,
      };
    });
  },
};
